import { useEffect } from "react";
import Cookies from "js-cookie";
import {
  createBankAccount,
  createCustomer,
  createInvoice,
  createQuote,
  updateProfile,
  getActiveSubscription,
} from "../../backend-services";
import { BankData, CustomerT } from "../../types";
import { ICreateSalesData, IUpdateProfile } from "../../@types";
import { useNavigate } from "react-router-dom";
import { useUserStore } from "../../state";
import AccountLoader from "../../components/auth/AccountLoader";
import { isObjectValuesEmpty } from "../../utils/ObjCheck";
import { handleError } from "../../utils/errorHandler";
import * as Sentry from "@sentry/react"; // Import Sentry
import { useCompanyStore } from "../../state/useCompanyStore";
import { createInvoiceFreeformData } from "../../modules/invoice/invoice";

const FreeInvoiceToolSetup = () => {
  const navigate = useNavigate();
  const { setCompanyBankAccounts, setCompany } = useCompanyStore();

  const handleErrorWithTracking = (
    error: unknown,
    context: string,
    extraData?: Record<string, any>,
    shouldNavigate = true,
  ) => {
    // 1) Log to console (optional)
    console.error(`Error in context: ${context}`, error);

    // 2) Capture with Sentry
    Sentry.captureException(error, {
      contexts: {
        freeInvoiceToolSetup: {
          context,
          ...extraData,
        },
      },
    });

    // 3) Optionally push data to GTM
    pushToDataLayer({
      event: "free_invoice_tool_error",
      error_message: error instanceof Error ? error.message : String(error),
      ...(extraData ?? {}),
      error_context: context,
      timestamp: new Date().toISOString(),
    });

    // 4) Possibly navigate to the error page
    if (shouldNavigate) {
      navigate("/invoices/createFreeform");
    }
  };

  // Helper function to safely push GTM events
  const pushToDataLayer = (eventData: Record<string, any>) => {
    try {
      if (window.dataLayer) {
        window.dataLayer.push(eventData);
      } else {
        console.warn("GTM DataLayer is not available");
      }
    } catch (error) {
      console.error("GTM Event Push Failed:", error);
      Sentry.captureException(error, { contexts: { GTM: eventData } });
    }
  };

  useEffect(() => {
    /**
     * 1) Send GTM event for page load:
     */

    const typeOfInvoice = Cookies.get("type") || "";
    const eventName =
      typeOfInvoice === "invoice"
        ? "free_invoice_tool_invoice"
        : typeOfInvoice === "quote"
          ? "free_invoice_tool_quote"
          : "free_invoice_tool_unknown_view";

    // Fire GTM event on page load
    pushToDataLayer({ event: eventName, timestamp: new Date().toISOString() });

    /**
     * 2) Helper to parse cookies with consistent error handling
     */
    const getParsedCookie = <T,>(cookieName: string): T | null => {
      const cookieValue = Cookies.get(cookieName);
      Sentry.addBreadcrumb({
        category: `cookies ${cookieName}`,
        message: "Parsed Cookie Values",
        data: {
          cookieName: cookieName,
          cookieValue: cookieValue,
        },
      });

      try {
        return cookieValue ? (JSON.parse(cookieValue) as T) : null;
      } catch (error) {
        handleErrorWithTracking(
          error,
          `Parsing Cookie: ${cookieName}`,
          { cookieValue },
          false,
        );
        return null;
      }
    };
    const type = Cookies.get("type") || "";
    const parsedInvoiceData = getParsedCookie<ICreateSalesData>("invoiceData");
    const parsedCustomerDetails = getParsedCookie<CustomerT>("customerDetails");
    const parsedBankDetails = getParsedCookie<BankData>("bankDetails");
    const parsedProfileData = getParsedCookie<IUpdateProfile>("profileData");

    /**
     * 3) Main function that orchestrates the creation calls
     */
    const handleCreateData = async (
      customerData: CustomerT,
      invoiceData: ICreateSalesData,
      bankData: BankData,
      profileData: IUpdateProfile,
      parsedType: string,
    ) => {
      try {
        // a) get the active subscription
        const activeSubscription = await getActiveSubscription();

        Sentry.addBreadcrumb({
          category: "subscription Check",
          message: "Getting active subscription",
          data: {
            activeSubscription: activeSubscription,
          },
        });

        // Check if the subscription is valid
        if (!activeSubscription || !activeSubscription.id) {
          const errorMessage = "No active subscription found.";
          throw new Error(errorMessage);
        }

        // b) Check for valid bank data
        const isBankDataValid = Boolean(
          bankData.accountName && bankData.accountNumber && bankData.bankName,
        );

        let profileResponse = null;
        let customerResponse = null;
        let bankAccountResponse = null;

        // Run updateProfile and createCustomer only if their data exists
        if (
          profileData &&
          profileData?.businessName &&
          profileData?.businessName?.length > 2
        ) {
          profileResponse = await updateProfile(profileData);
        }

        if (customerData) {
          customerResponse = await createCustomer(customerData);
        }

        // Create bank account if needed
        if (isBankDataValid) {
          bankAccountResponse = await createBankAccount(bankData);
        }

        // Only update company if profile was updated
        if (profileResponse) {
          const updatedProfile = {
            ...profileResponse,
            bankAccounts: bankAccountResponse?.data || [],
          };

          setCompany(updatedProfile);
        }

        // Throw if customer creation failed
        const customerId = customerResponse?.id;

        if (!customerId) {
          throw new Error("Failed to retrieve valid customerId");
        }

        // Prepare invoice data
        const businessType = profileResponse?.businessType;
        const isRetail = businessType === "retail";
        if (!businessType) {
          throw new Error("Failed to retrieve valid business Type");
        }
        const updatedInvoiceData = {
          ...invoiceData,
          customerId,
        };

        if (parsedType === "invoice") {
          const invoiceResponse = await createInvoiceFreeformData(
            invoiceData,
            customerResponse,
            isRetail,
          );
          // const invoiceResponse = await createInvoice(updatedInvoiceData);
          if (invoiceResponse && invoiceResponse.id) {
            pushToDataLayer({
              event: "free_invoice_tool_invoice_created",
              invoiceId: invoiceResponse.id,
              customerId,
            });
            removeCookies();
            navigate(`/invoices/${invoiceResponse.id}/view?freeForm=freeForm`);
          }
        } else if (parsedType === "quote") {
          const quoteResponse = await createQuote(updatedInvoiceData);
          if (quoteResponse && quoteResponse.id) {
            pushToDataLayer({
              event: "free_quote_tool_quote_created",
              quoteId: quoteResponse.id,
              customerId,
            });
            removeCookies();
            navigate(`/quotes/${quoteResponse.id}/view?freeForm=freeForm`);
          }
        } else {
          // If the type is not "invoice" or "quote", we consider it invalid
          throw new Error(`Invalid type in cookies: ${parsedType}`);
        }
      } catch (error) {
        handleErrorWithTracking(
          error,
          "FreeInvoiceToolSetup - handleCreateData",
        );
      }
    };

    /**
     * 4) Remove the cookies once done
     */
    const removeCookies = () => {
      [
        "type",
        "invoiceData",
        "customerDetails",
        "bankDetails",
        "navigation",
      ].forEach((cookieName) => {
        Cookies.remove(cookieName);
      });
    };

    /**
     * 5) Validate we have all needed cookies, then call handleCreateData
     */

    if (
      parsedCustomerDetails &&
      parsedInvoiceData &&
      parsedProfileData &&
      type
    ) {
      handleCreateData(
        parsedCustomerDetails,
        parsedInvoiceData,
        parsedBankDetails ?? ({} as BankData),
        parsedProfileData,
        type,
      );
    } else {
      const errorMessage = `Missing or invalid data in cookies.`;
      Sentry.addBreadcrumb({
        category: "Missing or invalid data in cookies.",
        message: "Error Check",
        data: {
          parsedCustomerDetails,
          parsedInvoiceData,
          parsedProfileData,
          type,
          parsedBankDetails,
        },
      });
      handleErrorWithTracking(
        new Error(errorMessage),
        "FreeInvoiceToolSetup - Missing Data",
        {
          parsedCustomerDetails,
          parsedInvoiceData,
          parsedBankDetails,
          parsedProfileData,
          type,
        },
      );
    }
  }, [navigate, setCompany, setCompanyBankAccounts]);

  return <AccountLoader />;
};

export default FreeInvoiceToolSetup;
